import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { CvApiDto } from '../contracts/backend-api.contracts';
import { CV_AUTOLLENADO_PORTAL_MOCK, CV_MOCK } from '../mocks/cv.mock';
import {
  anioDeFecha,
  BloqueCv,
  createEmptyCvData,
  CVData,
  fechaDesdeAnio,
} from '../models/cv.models';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class CvService {
  private cvData: CVData = this.clone(CV_MOCK);
  private readonly baseUrl = `${environment.apiBaseUrl}/perfil/cv`;

  constructor(private readonly http: HttpClient) {}

  getCv(): Observable<CVData> {
    if (!environment.useMockApi) {
      return this.http.get<CvApiDto>(this.baseUrl).pipe(map((dto) => this.fromApiDto(dto)));
    }

    return of(this.clone(this.cvData));
  }

  /**
   * PUT parcial de Curriculum.
   * El cliente envía solo el arreglo del acordeón activo y ultimo_bloque.
   * El mock/backend fusiona con lo ya persistido; las claves omitidas no se borren.
   * Docencia, idiomas y adjuntos no viajan en esta vista: no vaciarlos en el merge.
   * Los asteriscos son visuales; agregar un registro no exige completar el bloque.
   * TODO(backend): el API real debe hacer merge JSON (semántica PATCH sobre PUT) y
   * no devolver 422 por secciones ausentes. El POST /perfil/cv/autollenado solo
   * propone bloques; no persiste hasta que el usuario confirma.
   */
  guardarCv(payload: Partial<CVData>): Observable<CVData> {
    const fusionado = this.fusionar(this.cvData, payload);

    if (!environment.useMockApi) {
      const body = this.toPartialApiDto(payload);
      return this.http.put<CvApiDto>(this.baseUrl, body).pipe(map((dto) => this.fromApiDto(dto)));
    }

    this.cvData = fusionado;
    return of(this.clone(fusionado));
  }

  autollenarDesdePortal(): Observable<CVData> {
    if (!environment.useMockApi) {
      return this.http.post<CvApiDto>(`${this.baseUrl}/autollenado`, {}).pipe(map((dto) => this.fromApiDto(dto)));
    }

    return of(this.clone(CV_AUTOLLENADO_PORTAL_MOCK));
  }

  private fusionar(actual: CVData, parcial: Partial<CVData>): CVData {
    const siguiente = this.clone(actual);
    (Object.keys(parcial) as Array<keyof CVData>).forEach((clave) => {
      const valor = parcial[clave];
      if (valor !== undefined) {
        (siguiente as unknown as Record<string, unknown>)[clave] = Array.isArray(valor)
          ? JSON.parse(JSON.stringify(valor))
          : valor;
      }
    });
    return siguiente;
  }

  private fromApiDto(dto: CvApiDto): CVData {
    const base = createEmptyCvData();
    return {
      ...base,
      formacionAcademica: (dto.formacion_academica ?? []).map((i) => ({
        tipoAntecedente: i.tipo_antecedente ?? '',
        grado: i.grado ?? '',
        institucion: i.institucion ?? '',
        pais: i.pais ?? '',
        anioInicio: i.anio_inicio || anioDeFecha(i.fecha_inicio ?? ''),
        anioTermino: i.anio_termino || anioDeFecha(i.fecha_termino ?? ''),
        fechaInicio: i.fecha_inicio || fechaDesdeAnio(i.anio_inicio ?? ''),
        fechaTermino: i.fecha_termino || fechaDesdeAnio(i.anio_termino ?? ''),
        estado: i.estado ?? '',
      })),
      lineasInvestigacion: (dto.lineas_investigacion ?? []).map((i) => ({
        linea: i.linea ?? '',
        disciplinaArea: i.disciplina_area ?? '',
      })),
      experiencia: (dto.experiencia ?? []).map((i) => ({
        cargo: i.cargo ?? '',
        institucion: i.institucion ?? '',
        experienciaVigente: i.experiencia_vigente ?? '',
        funcionesPrincipales: i.funciones_principales ?? '',
        fechaInicio: i.fecha_inicio || '',
        fechaTermino: i.fecha_termino || '',
      })),
      publicaciones: (dto.publicaciones ?? []).map((i) => ({
        tipo: i.tipo ?? '',
        titulo: i.titulo ?? '',
        estructuraAutores: i.estructura_autores ?? '',
        revistaEditorial: i.revista_editorial ?? '',
        doiEnlace: i.doi_enlace ?? '',
        anio: i.anio || anioDeFecha(i.fecha ?? ''),
        fecha: i.fecha || fechaDesdeAnio(i.anio ?? ''),
      })),
      proyectos: (dto.proyectos ?? []).map((i) => ({
        nombreProyecto: i.nombre_proyecto ?? '',
        codigoFolio: i.codigo_folio ?? '',
        rol: i.rol ?? '',
        fuenteFinanciamiento: i.fuente_financiamiento ?? '',
        anioInicio: i.anio_inicio || anioDeFecha(i.fecha_inicio ?? ''),
        anioTermino: i.anio_termino || anioDeFecha(i.fecha_termino ?? ''),
        fechaInicio: i.fecha_inicio || fechaDesdeAnio(i.anio_inicio ?? ''),
        fechaTermino: i.fecha_termino || fechaDesdeAnio(i.anio_termino ?? ''),
      })),
      distinciones: (dto.distinciones ?? []).map((i) => ({
        nombreReconocimiento: i.nombre_reconocimiento ?? '',
        institucionOtorga: i.institucion_otorga ?? '',
        enlaceRespaldo: i.enlace_respaldo ?? '',
        anio: i.anio || anioDeFecha(i.fecha ?? ''),
        fecha: i.fecha || fechaDesdeAnio(i.anio ?? ''),
        descripcion: i.descripcion ?? '',
      })),
      docencia: (dto.docencia ?? []).map((i) => ({
        curso: i.curso ?? '',
        institucion: i.institucion ?? '',
        periodo: i.periodo ?? '',
      })),
      idiomas: (dto.idiomas ?? []).map((i) => ({
        idioma: i.idioma ?? '',
        nivel: i.nivel ?? '',
      })),
      adjuntos: (dto.adjuntos ?? []).map((i) => ({
        tipo: i.tipo ?? 'cv_pdf',
        nombreArchivo: i.nombre_archivo ?? '',
        sizeKb: i.size_kb ?? 0,
      })),
      ultimoBloque: this.comoBloqueCv(dto.ultimo_bloque),
    };
  }

  private toPartialApiDto(model: Partial<CVData>): CvApiDto {
    const dto: CvApiDto = {};
    if (model.formacionAcademica) {
      dto.formacion_academica = model.formacionAcademica.map((i) => ({
        tipo_antecedente: i.tipoAntecedente,
        grado: i.grado,
        institucion: i.institucion,
        pais: i.pais,
        anio_inicio: i.anioInicio || anioDeFecha(i.fechaInicio),
        anio_termino: i.anioTermino || anioDeFecha(i.fechaTermino),
        fecha_inicio: i.fechaInicio,
        fecha_termino: i.fechaTermino,
        estado: i.estado,
      }));
    }
    if (model.lineasInvestigacion) {
      dto.lineas_investigacion = model.lineasInvestigacion.map((i) => ({
        linea: i.linea,
        disciplina_area: i.disciplinaArea,
      }));
    }
    if (model.experiencia) {
      dto.experiencia = model.experiencia.map((i) => ({
        cargo: i.cargo,
        institucion: i.institucion,
        experiencia_vigente: i.experienciaVigente,
        funciones_principales: i.funcionesPrincipales,
        fecha_inicio: i.fechaInicio,
        fecha_termino: i.fechaTermino,
      }));
    }
    if (model.publicaciones) {
      dto.publicaciones = model.publicaciones.map((i) => ({
        tipo: i.tipo,
        titulo: i.titulo,
        estructura_autores: i.estructuraAutores,
        revista_editorial: i.revistaEditorial,
        doi_enlace: i.doiEnlace,
        anio: i.anio || anioDeFecha(i.fecha),
        fecha: i.fecha,
      }));
    }
    if (model.proyectos) {
      dto.proyectos = model.proyectos.map((i) => ({
        nombre_proyecto: i.nombreProyecto,
        codigo_folio: i.codigoFolio,
        rol: i.rol,
        fuente_financiamiento: i.fuenteFinanciamiento,
        anio_inicio: i.anioInicio || anioDeFecha(i.fechaInicio),
        anio_termino: i.anioTermino || anioDeFecha(i.fechaTermino),
        fecha_inicio: i.fechaInicio,
        fecha_termino: i.fechaTermino,
      }));
    }
    if (model.distinciones) {
      dto.distinciones = model.distinciones.map((i) => ({
        nombre_reconocimiento: i.nombreReconocimiento,
        institucion_otorga: i.institucionOtorga,
        enlace_respaldo: i.enlaceRespaldo,
        anio: i.anio || anioDeFecha(i.fecha),
        fecha: i.fecha,
        descripcion: i.descripcion,
      }));
    }
    if (model.docencia) {
      dto.docencia = model.docencia.map((i) => ({
        curso: i.curso,
        institucion: i.institucion,
        periodo: i.periodo,
      }));
    }
    if (model.idiomas) {
      dto.idiomas = model.idiomas.map((i) => ({
        idioma: i.idioma,
        nivel: i.nivel,
      }));
    }
    if (model.adjuntos) {
      dto.adjuntos = model.adjuntos.map((i) => ({
        tipo: i.tipo,
        nombre_archivo: i.nombreArchivo,
        size_kb: i.sizeKb,
      }));
    }
    if (model.ultimoBloque !== undefined) {
      dto.ultimo_bloque = model.ultimoBloque;
    }
    return dto;
  }

  private comoBloqueCv(valor: string | undefined): BloqueCv | '' {
    if (
      valor === 'estudios' ||
      valor === 'lineas' ||
      valor === 'experiencia' ||
      valor === 'proyectos' ||
      valor === 'publicaciones' ||
      valor === 'reconocimientos'
    ) {
      return valor;
    }
    return '';
  }

  private clone(data: CVData): CVData {
    return JSON.parse(JSON.stringify(data)) as CVData;
  }
}
